import { identity } from 'lodash-es';
import { filter, map, pipe, scan } from 'rxjs';
import type { MinimalSubscriptionResponse } from '../types/SubscriptionResponse';
import { isRemoveSubscriptionUpdate } from './utils';

type RelevancePredicate<T, TRelevant extends T> = ((item: T) => boolean) | ((item: T) => item is TRelevant);

export interface WSScanToDoubleMapParams<
  T,
  KeyType1 extends string | number,
  KeyType2 extends string | number,
  P,
  TRelevant extends T = T,
> {
  /** Get the first key to use at the first level of indexing */
  getKey1: (item: T) => KeyType1;
  /** Get the second key to use at the second level of indexing */
  getKey2: (item: T) => KeyType2;
  /**
   * Whether or not to return a new map instance on every data update, treating it as an immutable map
   * Useful when the instance of the map is used to trigger change detection.
   */
  newOuterMapEachUpdate: boolean;
  /**
   * Whether or not to create new map instances of the inner maps as well on each update, treating it as an immutable map
   * Only the changed inner maps will have new instances after some SubscriptionResponse update is handled.
   * Useful if you have change detection dependent on some inner map.
   */
  newInnerMapsEachUpdate: boolean;
  /**
   * An optional parameter which allows you to take full control over the deletion of items.
   * If provided, this function is the only code path for deletion. Any potential UpdateAction: "Remove" on the incoming entries will be ignored
   * by the pipe itself for instance.
   * @param item the item in question, coming from json.data
   * @param message the subscription response containing the item
   * @returns whether or not to delete the given item
   */
  shouldDelete?: (item: T, message: MinimalSubscriptionResponse<T>) => boolean;
  /**
   * Whether an update is relevant to this index. Irrelevant updates are ignored
   * unless their keys already exist in the map, in which case shouldDelete can
   * remove the stale entry. This supports updates where an entry's relevant
   * properties change and it should leave the index. When provided, unchanged
   * messages do not emit.
   */
  isRelevant?: RelevancePredicate<T, TRelevant>;
  /**
   * Whether or not to delete an inner map when it is emptied (size = 0).
   * Defaults to false.
   */
  deleteInnerMapOnEmptied?: boolean;
  /** An optional function going from `T => P` where you determine what you would like stored as values in the map.
   * @example
   * // basic case: just store T. This will emit a double map of type `Map<KeyType1, Map<KeyType2, T>`
   * getInsertable: item => item
   * // store a specific property of T, eg DisplayName. This will emit a map of type `Map<KeyType1, Map<KeyType2, string>`
   * getInsertable: item => item.DisplayName
   * @default lodash.identity
   */
  getInsertable?: (item: TRelevant) => P;
}

/**
 * An RxJS operator to scan SubscriptionResponses to a "double-map", meaning a two-leveled map.
 * Useful in cases where you want to index data with two keys.
 * @returns `Map<string | number, Map<string | number, T>>`
 */
export function wsScanToDoubleMap<
  T extends AnyObject,
  KeyType1 extends string | number,
  KeyType2 extends string | number,
  P = T,
  TRelevant extends T = T,
>({
  getKey1,
  getKey2,
  newOuterMapEachUpdate,
  newInnerMapsEachUpdate,
  shouldDelete,
  isRelevant,
  deleteInnerMapOnEmptied = false,
  getInsertable = identity,
}: WSScanToDoubleMapParams<T, KeyType1, KeyType2, P, TRelevant>) {
  const isRelevantUpdate = (update: T): update is TRelevant => isRelevant?.(update) ?? true;

  return pipe(
    scan(
      ({ map: outerMapAcc }, json: MinimalSubscriptionResponse<T>) => {
        // if initial always create a new map
        let outerMap = json.initial === true ? new Map<KeyType1, Map<KeyType2, P>>() : outerMapAcc;
        let changedOuterMap = !!json.initial;
        let didChange = !!json.initial;

        // Wrapping the map accessor like this for the purpose of writing makes sure we only ever re-create the outer map once we know that we'll be writing to it
        const getWritableOuterMap = () => {
          if (newOuterMapEachUpdate && !changedOuterMap) {
            outerMap = new Map(outerMap);
            changedOuterMap = true;
          }
          return outerMap;
        };

        // Be defensive against inconsistent API responses that omit data.
        if (json.data == null) {
          return { map: outerMap, didChange };
        }

        const changedInnerMaps = new Set<KeyType1>();
        for (const update of json.data) {
          const key1 = getKey1(update);
          const key2 = getKey2(update);
          const currentInnerMap = outerMap.get(key1);
          const updateIsRelevant = isRelevantUpdate(update);
          if (!updateIsRelevant && currentInnerMap?.has(key2) !== true) {
            continue;
          }

          // If specified, the shouldDelete function has full control over _all_ deletion
          const shouldDeleteUpdate = shouldDelete
            ? shouldDelete(update, json)
            : isRemoveSubscriptionUpdate(update, json);

          // if the inner map is already marked as changed, we don't need to create a new instance of it.
          const shouldUpdateInnerMapThisUpdate = newInnerMapsEachUpdate && !changedInnerMaps.has(key1);

          if (shouldDeleteUpdate) {
            // Should delete. Try to grab the inner map first.
            if (!currentInnerMap?.has(key2)) {
              continue;
            }

            const innerMap = shouldUpdateInnerMapThisUpdate ? new Map(currentInnerMap) : currentInnerMap;
            innerMap.delete(key2);
            getWritableOuterMap().set(key1, innerMap);
            didChange = true;

            if (deleteInnerMapOnEmptied && innerMap.size === 0) {
              getWritableOuterMap().delete(key1);
              // in case messages are poorly coalesced, in which case there can be updates + deletions in one single data array,
              // make sure to delete any possible entry of a changed map related to the map we're now deleting
              changedInnerMaps.delete(key1);
              continue;
            }

            // We only add the map to set of changed inner maps if we don't end up deleting it directly above ^
            changedInnerMaps.add(key1);
          } else {
            if (!updateIsRelevant) {
              continue;
            }
            if (currentInnerMap != null) {
              const innerMap = shouldUpdateInnerMapThisUpdate ? new Map(currentInnerMap) : currentInnerMap;
              // we're making a change to an already created map
              innerMap.set(key2, getInsertable(update));
              getWritableOuterMap().set(key1, innerMap);
              changedInnerMaps.add(key1);
            } else {
              // immediately set the item in a new innerMap and dont add this map to the changedInnerMaps set
              // since it implicitly changes due to being... created :)
              getWritableOuterMap().set(key1, new Map([[key2, getInsertable(update)]]));
            }
            didChange = true;
          }
        }

        return { map: outerMap, didChange };
      },
      { map: new Map<KeyType1, Map<KeyType2, P>>(), didChange: false }
    ),
    filter(({ didChange }) => isRelevant == null || didChange),
    map(({ map }) => map)
  );
}

/** Returns all values stored in the inner map for the provided outer key. */
export function getDoubleMapValues<TKey1, TKey2, TValue>(map: Map<TKey1, Map<TKey2, TValue>>, key: TKey1): TValue[] {
  return Array.from(map.get(key)?.values() ?? []);
}
